import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { Alert } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import { Field } from "../../components/ui/Field";
import { ApiRequestError, apiFetch, clearApiSession, setCsrfToken } from "../../api/client";
import styles from "./Auth.module.css";

const passwordSchema = z.string().min(12, "Password must be at least 12 characters");
const emailSchema = z.string().email("Enter a valid email address");

function fragmentToken(): string {
  const value = new URLSearchParams(window.location.hash.slice(1)).get("token") || "";
  return value;
}

function clearFragment(): void {
  window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);
}

function errorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof ApiRequestError)) return fallback;
  if (error.status === 429) return "Please wait a moment and try again.";
  return error.payload?.detail || fallback;
}

function AuthLayout({ eyebrow, title, children }: { eyebrow: string; title: string; children: ReactNode }) {
  return <section className={styles.shell} aria-labelledby="auth-heading"><header className={styles.header}><p className="eyebrow">{eyebrow}</p><h1 id="auth-heading">{title}</h1></header>{children}</section>;
}

function AuthMessage({ message, tone = "error" }: { message: string; tone?: "error" | "success" | "info" }) {
  return <div className={styles.message}><Alert tone={tone}>{message}</Alert></div>;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault(); setMessage("");
    const emailResult = emailSchema.safeParse(email);
    if (!emailResult.success) { setMessage(emailResult.error.issues[0]?.message || "Enter a valid email address"); return; }
    if (!password) { setMessage("Enter your password"); return; }
    setBusy(true);
    try {
      const result = await apiFetch<{ csrf_token: string }>("/api/auth/login", { method: "POST", body: { email, password } });
      setCsrfToken(result.csrf_token);
      const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
      navigate(from?.startsWith("/app") ? from : "/app", { replace: true });
    } catch (error) { setMessage(errorMessage(error, "Email or password is incorrect")); }
    finally { setBusy(false); }
  }
  return <AuthLayout eyebrow="Your account" title="Sign in"><form className={styles.form} onSubmit={submit} noValidate>
    {message && <AuthMessage message={message} />}
    <Field label="Email" name="email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
    <Field label="Password" name="password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
    <div className={styles.actions}><Button type="submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</Button><Link to="/auth/forgot-password">Forgot password?</Link></div>
  </form><nav className={styles.links} aria-label="Account help"><Link to="/auth/resend-verification">Resend verification email</Link><Link to="/">Back to planner</Link></nav></AuthLayout>;
}

function PasswordPage({ invitation }: { invitation: boolean }) {
  const navigate = useNavigate();
  const [token] = useState(() => fragmentToken());
  const [password, setPassword] = useState(""); const [confirmation, setConfirmation] = useState("");
  const [message, setMessage] = useState(""); const [success, setSuccess] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { clearFragment(); }, []);
  async function submit(event: FormEvent) {
    event.preventDefault(); setMessage(""); setSuccess("");
    if (!token) { setMessage(`This ${invitation ? "invitation" : "reset link"} is missing or invalid.`); return; }
    const result = passwordSchema.safeParse(password);
    if (!result.success) { setMessage(result.error.issues[0]?.message || "Choose a stronger password"); return; }
    if (password !== confirmation) { setMessage("Passwords do not match"); return; }
    setBusy(true);
    try {
      await apiFetch(invitation ? "/api/auth/register" : "/api/auth/reset-password", { method: "POST", body: { token, password } });
      clearFragment(); setSuccess(invitation ? "Your account is ready. You can now sign in." : "Your password has been reset. You can now sign in.");
      setTimeout(() => navigate("/auth/login", { replace: true }), 0);
    } catch (error) { setMessage(errorMessage(error, invitation ? "This invitation is invalid or expired." : "This reset link is invalid or expired.")); }
    finally { setBusy(false); }
  }
  return <AuthLayout eyebrow="Your account" title={invitation ? "Accept invitation" : "Reset password"}>{message && <AuthMessage message={message} />}{success && <AuthMessage tone="success" message={success} />}<form className={styles.form} onSubmit={submit} noValidate>
    <Field label="New password" name="password" type="password" autoComplete="new-password" hint="Use at least 12 characters." value={password} onChange={(event) => setPassword(event.target.value)} required />
    <Field label="Confirm password" name="password-confirmation" type="password" autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required />
    <div className={styles.actions}><Button type="submit" disabled={busy}>{busy ? "Saving…" : invitation ? "Create account" : "Reset password"}</Button><Link to="/auth/login">Back to sign in</Link></div>
  </form></AuthLayout>;
}

export function EmailActionPage({ resend }: { resend: boolean }) {
  const [email, setEmail] = useState(""); const [message, setMessage] = useState(""); const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault(); setMessage(""); const result = emailSchema.safeParse(email);
    if (!result.success) { setMessage(result.error.issues[0]?.message || "Enter a valid email address"); return; }
    setBusy(true);
    try { await apiFetch(`/api/auth/${resend ? "resend-verification" : "forgot-password"}`, { method: "POST", body: { email } }); setMessage(resend ? "If the account exists, a verification email has been sent." : "If the account exists, a password reset email has been sent."); }
    catch (error) { setMessage(errorMessage(error, "Please wait a moment and try again.")); }
    finally { setBusy(false); }
  }
  return <AuthLayout eyebrow="Your account" title={resend ? "Resend verification" : "Reset password"}><p className={styles.muted}>{resend ? "Enter your email and we’ll send another verification link." : "Enter your email and we’ll send a password reset link if the account exists."}</p>{message && <AuthMessage tone={message.startsWith("If ") ? "success" : "error"} message={message} />}<form className={styles.form} onSubmit={submit} noValidate><Field label="Email" name="email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /><div className={styles.actions}><Button type="submit" disabled={busy}>{busy ? "Sending…" : resend ? "Send verification link" : "Send reset link"}</Button><Link to="/auth/login">Back to sign in</Link></div></form></AuthLayout>;
}

export function VerifyPage() {
  const [message, setMessage] = useState("Verifying your email…"); const [tone, setTone] = useState<"info" | "success" | "error">("info");
  const [token] = useState(() => fragmentToken());
  useEffect(() => { clearFragment(); if (!token) { setMessage("This verification link is missing or invalid."); setTone("error"); return; } apiFetch("/api/auth/verify", { method: "POST", body: { token } }).then(() => { setMessage("Your email is verified. You can now sign in."); setTone("success"); }).catch((error: unknown) => { setMessage(errorMessage(error, "This verification link is invalid or expired.")); setTone("error"); }); }, [token]);
  return <AuthLayout eyebrow="Your account" title="Verify email"><AuthMessage message={message} tone={tone} /><nav className={styles.links}><Link to="/auth/login">Go to sign in</Link><Link to="/auth/resend-verification">Send another verification email</Link></nav></AuthLayout>;
}

export function LogoutAction() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  async function logout() {
    setMessage(""); setBusy(true);
    try {
      await apiFetch("/api/auth/logout", { method: "POST", csrf: true });
      clearApiSession(); queryClient.clear(); navigate("/auth/login", { replace: true });
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 401) {
        clearApiSession(); queryClient.clear(); navigate("/auth/login", { replace: true });
      } else setMessage("We couldn’t sign you out. Please try again.");
    } finally { setBusy(false); }
  }
  return <>{message && <AuthMessage message={message} />}<Button variant="secondary" onClick={logout} disabled={busy}>{busy ? "Signing out…" : "Log out"}</Button></>;
}

export { PasswordPage };
